import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';

@Component({
  selector: 'app-camera-capture-dialog',
  templateUrl: './camera-capture-dialog.component.html',
  styleUrls: ['./camera-capture-dialog.component.scss'],
})
export class CameraCaptureDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() captured = new EventEmitter<File>();

  @ViewChild('videoElement') videoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;

  cameraError = false;
  isReady = false;

  // 'environment' 表示使用後置鏡頭，'user' 表示使用前置鏡頭
  private facingMode: 'environment' | 'user' = 'environment';
  private stream?: MediaStream;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && !changes['visible'].firstChange && this.visible) {
      this.cameraError = false;
      // 等 p-dialog 內容渲染出 <video> 之後再啟動串流
      setTimeout(() => this.startCamera());
    }
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  async switchCamera(): Promise<void> {
    this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
    await this.startCamera();
  }

  capture(): void {
    const video = this.videoRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;
    if (!video || !canvas || !video.videoWidth) {
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }
      const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
      this.close();
      this.captured.emit(file);
    }, 'image/jpeg', 0.92);
  }

  close(): void {
    this.stopCamera();
    this.visibleChange.emit(false);
  }

  private async startCamera(): Promise<void> {
    this.stopCamera();
    const video = this.videoRef?.nativeElement;
    if (!video) {
      return;
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: this.facingMode },
      });
      // 等串流的期間視窗可能已經被關掉，這時要把剛取得的串流直接釋放
      if (!this.visible) {
        this.stopCamera();
        return;
      }
      video.srcObject = this.stream;
      await video.play();
      this.isReady = true;
      this.cameraError = false;
    } catch (error) {
      console.error('Error accessing camera:', error);
      this.cameraError = true;
      this.isReady = false;
    }
  }

  private stopCamera(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = undefined;
    this.isReady = false;
    const video = this.videoRef?.nativeElement;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
  }
}
